<template>
  <v-autocomplete
      :error-messages="error"
      variant="outlined"
      rounded="lg"
      bg-color="surface"
      :label="translation(translationList.vehicle, 'Vehicul')"
      :items="vehicles"
      :item-title="getItemTitle"
      name="vehicle"
      item-value="id"
      :loading="loading"
      :disabled="loading"
      v-model="selectedVehicle"
      :clearable="canBeCleared"
      :rules="[v => !!v || translation(translationList.required, 'Acest camp este obligatoriu')]"
      return-object
  >
  </v-autocomplete>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, toRefs, watch, computed } from 'vue';
import { useTranslationStore, translation } from "~/plugins/store/translations";
import type { ApiVehicleDetails, BasicVehicle } from "~/types/Vehicle";
import type { TranslationList } from "~/types/Utils";

export default defineComponent({
  props: {
    canBeCleared: Boolean,
    modelValue: {
      type: Object as () => BasicVehicle | null,
      default: () => null,
      required: false
    },
    vehicles: {
      type: Array as () => BasicVehicle[] | ApiVehicleDetails[],
      default: () => [],
      required: true
    }
  },
  emits: ['update:modelValue', 'validation-vehicle-status'],
  setup(props, { emit }) {
    const { modelValue, vehicles } = toRefs(props);
    const translationStore = useTranslationStore();
    const selectedVehicle = ref(modelValue.value && modelValue.value.registration_plate ? modelValue.value : null);
    const translationList = ref<TranslationList>({});
    const loading = ref(false);
    const error = ref([]);

    const getItemTitle = (item: ApiVehicleDetails) => {
      let result =  (item.registration_plate || item.vin);
      if (typeof item.rca_vehicle_make_name !== 'undefined' && typeof item.rca_vehicle_model_name !== 'undefined') {
        result += ` - ${item.rca_vehicle_make_name} ${item.rca_vehicle_model_name}`;
      }
      return result;
    };

    onMounted(async () => {
      loading.value = true;
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      if (!modelValue.value && vehicles.value.length > 0) {
        selectedVehicle.value = vehicles.value[0];
        emit('update:modelValue', selectedVehicle.value);
      }
      loading.value = false;
      const isValid = !!selectedVehicle.value;
      emit('validation-vehicle-status', { field: 'vehicle', isValid });

      window.addEventListener('validate-vehicle', handleVehicleValidationEvent);
      window.addEventListener('validate-travel', handleVehicleValidationEvent);
    });

    const handleVehicleValidationEvent = (data: any) => {
      if (data.detail && data.detail === 'vehicle') {
        if (!selectedVehicle.value?.registration_plate) {
          error.value.push(translation(translationList.value.required, 'Acest câmp este obligatoriu'));
        } else {
          error.value = [];
          emit('validation-vehicle-status', { field: 'vehicle', isValid: true });
        }
      }
    };

    watch(selectedVehicle, (newValue) => {
      emit('update:modelValue', newValue);
    });

    watch(modelValue, (newValue) => {
      selectedVehicle.value = newValue;
      if (newValue && newValue.registration_plate) {
        error.value = [];
      }

      const isValid = !!newValue;
      emit('validation-vehicle-status', { field: 'vehicle', isValid });
    });

    return {
      selectedVehicle,
      translationList,
      translation,
      loading,
      error,
      getItemTitle,
    };
  },
});
</script>

<style scoped>
</style>